/**
 * Auth provider factory.
 *
 * Returns the correct IAuthProvider implementation based on
 * `authConfig.provider` (driven by VITE_AUTH_PROVIDER env var).
 *
 * To add a new provider:
 *   1. Add its type to AuthProviderType in types/auth.types.ts
 *   2. Create a class that implements IAuthProvider
 *   3. Add a case here
 *   4. Set VITE_AUTH_PROVIDER=<type> in .env
 */

import type { IAuthProvider } from '../types/auth.types';
import { authConfig } from './config';
import type { AuthConfig } from './config';
import { AzureAuthProvider } from './providers/AzureAuthProvider';

/**
 * Create an IAuthProvider instance for the configured provider type.
 *
 * Azure is eagerly imported (it's the current default).
 * Future providers (Auth0, Okta) will be added as new case branches.
 */
export function createAuthProvider(config: AuthConfig = authConfig): IAuthProvider {
  switch (config.provider) {
    case 'azure':
      return new AzureAuthProvider(config);

    case 'auth0':
      throw new Error(
        'Auth0 provider not yet implemented. Set VITE_AUTH_PROVIDER=azure or implement src/auth/providers/Auth0AuthProvider.ts',
      );

    case 'okta':
      throw new Error(
        'Okta provider not yet implemented. Set VITE_AUTH_PROVIDER=azure or implement src/auth/providers/OktaAuthProvider.ts',
      );

    case 'mock':
      throw new Error(
        'Mock provider not yet implemented. Implement src/auth/providers/MockAuthProvider.ts for testing.',
      );

    default: {
      const _exhaustive: never = config.provider;
      throw new Error(`Unknown auth provider: ${_exhaustive}`);
    }
  }
}

// Re-export the pieces that the rest of the app needs
export { authConfig } from './config';
export type { AuthConfig } from './config';
export { AzureAuthProvider } from './providers/AzureAuthProvider';
